import React, { useEffect, useRef } from "react";
import "./CinematicBg.css";

const CinematicBg = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    nodesRef.current = [];
    particlesRef.current = [];

    const stars = Array.from({ length: 130 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.2 + 0.2,
      opacity: Math.random() * 0.35 + 0.12,
    }));

    // Network nodes class
    class NetworkNode {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 1.5 + 0.5;
        this.opacity = Math.random() * 0.6 + 0.2;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.pulseSpeed = Math.random() * 0.015 + 0.008;
        this.color = Math.random() > 0.85 ? "rgba(239, 68, 68, opacity)" : "rgba(200, 180, 220, opacity)";
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.pulsePhase += this.pulseSpeed;

        // Bounce off edges
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        this.x = Math.max(0, Math.min(canvas.width, this.x));
        this.y = Math.max(0, Math.min(canvas.height, this.y));
      }

      draw() {
        const pulse = Math.sin(this.pulsePhase) * 0.4 + 0.6;
        const finalOpacity = this.opacity * pulse;
        const colorStr = this.color.replace("opacity", finalOpacity);

        // Glow
        ctx.shadowColor = this.color.includes("239, 68") ? "rgba(239, 68, 68, 0.6)" : "rgba(200, 180, 220, 0.3)";
        ctx.shadowBlur = 12;

        ctx.fillStyle = colorStr;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius + pulse * 0.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowColor = "transparent";
      }
    }

    // Particle class for energy burst
    class EnergyParticle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 3;
        this.vy = (Math.random() - 0.5) * 3;
        this.life = 1;
        this.decay = Math.random() * 0.01 + 0.008;
        this.size = Math.random() * 3 + 2;
        this.hue = Math.random() > 0.7 ? "red" : "orange";
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.98;
        this.vy *= 0.98;
        this.life -= this.decay;
      }

      draw() {
        if (this.life <= 0) return;

        const color = this.hue === "red" 
          ? `rgba(239, 68, 68, ${this.life * 0.8})`
          : `rgba(255, 100, 68, ${this.life * 0.6})`;

        ctx.shadowColor = `rgba(239, 68, 68, ${this.life * 0.4})`;
        ctx.shadowBlur = 20;

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * this.life, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowColor = "transparent";
      }

      isDead() {
        return this.life <= 0;
      }
    }

    // Initialize network nodes
    const nodeCount = 60;
    for (let i = 0; i < nodeCount; i++) {
      nodesRef.current.push(new NetworkNode());
    }

    // Create red energy burst epicenter
    let energyBurstX = canvas.width * 0.69;
    let energyBurstY = canvas.height * 0.43;

    let frameCount = 0;
    let animationId;

    const animate = () => {
      // Dark cosmic background with gradient
      const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      bgGradient.addColorStop(0, "#0a0e27");
      bgGradient.addColorStop(0.5, "#0f1522");
      bgGradient.addColorStop(1, "#0d1628");
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add subtle radial gradient from energy burst area
      const energyGradient = ctx.createRadialGradient(energyBurstX, energyBurstY, 50, energyBurstX, energyBurstY, 600);
      energyGradient.addColorStop(0, "rgba(239, 68, 68, 0.08)");
      energyGradient.addColorStop(0.3, "rgba(239, 68, 68, 0.04)");
      energyGradient.addColorStop(1, "rgba(239, 68, 68, 0)");
      ctx.fillStyle = energyGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw subtle depth stars each frame so they remain visible.
      for (const star of stars) {
        ctx.fillStyle = `rgba(180, 160, 200, ${star.opacity})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      }

      // Update and draw network nodes
      nodesRef.current.forEach((node) => {
        node.update();
        node.draw();
      });

      // Draw network connections
      ctx.strokeStyle = "rgba(239, 68, 68, 0.08)";
      ctx.lineWidth = 0.8;
      const connectionDistance = 180;

      for (let i = 0; i < nodesRef.current.length; i++) {
        for (let j = i + 1; j < nodesRef.current.length; j++) {
          const dx = nodesRef.current[i].x - nodesRef.current[j].x;
          const dy = nodesRef.current[i].y - nodesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.15;
            const isRed = nodesRef.current[i].color.includes("239, 68") || nodesRef.current[j].color.includes("239, 68");
            ctx.strokeStyle = isRed 
              ? `rgba(239, 68, 68, ${opacity})` 
              : `rgba(220, 180, 240, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodesRef.current[i].x, nodesRef.current[i].y);
            ctx.lineTo(nodesRef.current[j].x, nodesRef.current[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw energy burst core (central glowing element)
      const energyPulse = Math.sin(frameCount * 0.005) * 0.3 + 0.7;
      
      // Inner red core
      ctx.shadowColor = "rgba(239, 68, 68, 0.8)";
      ctx.shadowBlur = 40;
      ctx.fillStyle = `rgba(239, 68, 68, ${0.4 * energyPulse})`;
      ctx.beginPath();
      ctx.arc(energyBurstX, energyBurstY, 80 * energyPulse, 0, Math.PI * 2);
      ctx.fill();

      // Middle glow ring
      ctx.strokeStyle = `rgba(239, 68, 68, ${0.3 * energyPulse})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(energyBurstX, energyBurstY, 120 * energyPulse, 0, Math.PI * 2);
      ctx.stroke();

      // Outer glow ring
      ctx.strokeStyle = `rgba(255, 100, 68, ${0.15 * energyPulse})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(energyBurstX, energyBurstY, 180 * energyPulse, 0, Math.PI * 2);
      ctx.stroke();

      ctx.shadowColor = "transparent";

      // Emit energy particles occasionally
      if (frameCount % 8 === 0) {
        for (let i = 0; i < 3; i++) {
          particlesRef.current.push(new EnergyParticle(energyBurstX, energyBurstY));
        }
      }

      // Update and draw energy particles
      particlesRef.current = particlesRef.current.filter(p => !p.isDead());
      particlesRef.current.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      // Draw connecting lines from burst to nearby nodes
      nodesRef.current.forEach((node) => {
        const dx = energyBurstX - node.x;
        const dy = energyBurstY - node.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 300 && distance > 50) {
          const opacity = (1 - distance / 300) * 0.08;
          ctx.strokeStyle = `rgba(239, 68, 68, ${opacity})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(energyBurstX, energyBurstY);
          ctx.lineTo(node.x, node.y);
          ctx.stroke();
        }
      });

      frameCount++;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      energyBurstX = canvas.width * 0.69;
      energyBurstY = canvas.height * 0.43;

      for (const star of stars) {
        star.x = Math.random() * canvas.width;
        star.y = Math.random() * canvas.height;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} className="cinematic-bg-canvas" />;
};

export default CinematicBg;
